/**
 * Allocation Page
 * Main page for portfolio allocation tracking feature (MVP+1)
 *
 * Requires Pro/Family subscription (feature:allocation_tracking entitlement)
 */
import { useAllocationData } from '@/hooks/useAllocation';
import { useAllocationStore, selectActiveTab } from '@/store/allocationStore';
import Alert from '@/components/ui/Alert';
import Button from '@/components/ui/Button';
import {
  PortfolioAllocationView,
  BucketAllocationView,
  AccountTargetsView,
  HoldingsView,
  WarningsView,
  AllocationSkeleton,
  TemplateModal,
  AccountTargetModal,
  ClassifyHoldingModal,
  BulkClassifyModal,
  BucketAssignmentModal,
  ConfigurationModal,
  SettingsIcon,
} from '@/components/allocation';

type TabKey = 'portfolio' | 'buckets' | 'targets' | 'holdings' | 'warnings';

const TABS: { key: TabKey; label: string; description: string }[] = [
  { key: 'portfolio', label: 'Portfolio', description: 'Asset class allocation' },
  { key: 'buckets', label: 'Buckets', description: 'Strategic roles' },
  { key: 'targets', label: 'Targets', description: 'Account targets' },
  { key: 'holdings', label: 'Holdings', description: 'Classification' },
  { key: 'warnings', label: 'Warnings', description: 'Alerts' },
];

export default function AllocationPage() {
  const activeTab = useAllocationStore(selectActiveTab);
  const setActiveTab = useAllocationStore((state) => state.setActiveTab);
  const showTemplateModal = useAllocationStore((state) => state.ui.showTemplateModal);
  const setShowTemplateModal = useAllocationStore((state) => state.setShowTemplateModal);
  const showConfigModal = useAllocationStore((state) => state.ui.showConfigModal);
  const setShowConfigModal = useAllocationStore((state) => state.setShowConfigModal);

  // Modal states
  const editingAccountId = useAllocationStore((state) => state.ui.editingAccountId);
  const setEditingAccountId = useAllocationStore((state) => state.setEditingAccountId);
  const assigningAccountId = useAllocationStore((state) => state.ui.assigningAccountId);
  const setAssigningAccountId = useAllocationStore((state) => state.setAssigningAccountId);
  const classifyingHoldingId = useAllocationStore((state) => state.ui.classifyingHoldingId);
  const setClassifyingHoldingId = useAllocationStore((state) => state.setClassifyingHoldingId);
  const showBulkClassifyModal = useAllocationStore((state) => state.ui.showBulkClassifyModal);
  const setShowBulkClassifyModal = useAllocationStore((state) => state.setShowBulkClassifyModal);

  // Fetch all allocation data
  const { isLoading, isError, portfolio, warnings } = useAllocationData();

  // Get active warning count for badge
  const activeWarningsCount = warnings.data?.summary?.total ?? 0;

  const handleRefresh = () => {
    portfolio.refetch();
  };

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader
          onTemplateClick={() => setShowTemplateModal(true)}
          onRefreshClick={handleRefresh}
          onConfigureClick={() => setShowConfigModal(true)}
        />
        <Alert
          variant="error"
          title="Failed to load allocation data"
          className="mt-6"
        >
          There was a problem loading your allocation data. Please try again.
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        onTemplateClick={() => setShowTemplateModal(true)}
        onRefreshClick={handleRefresh}
        onConfigureClick={() => setShowConfigModal(true)}
      />

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-4 md:space-x-8 overflow-x-auto scrollbar-hide" aria-label="Allocation tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex-shrink-0
                ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              aria-current={activeTab === tab.key ? 'page' : undefined}
            >
              <span className="flex items-center gap-2">
                {tab.label}
                {tab.key === 'warnings' && activeWarningsCount > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {activeWarningsCount}
                  </span>
                )}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="mt-6">
        {isLoading ? (
          <AllocationSkeleton tab={activeTab} />
        ) : (
          <>
            {activeTab === 'portfolio' && <PortfolioAllocationView />}
            {activeTab === 'buckets' && <BucketAllocationView />}
            {activeTab === 'targets' && <AccountTargetsView />}
            {activeTab === 'holdings' && <HoldingsView />}
            {activeTab === 'warnings' && <WarningsView />}
          </>
        )}
      </div>

      {/* Template modal */}
      <TemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
      />

      {/* Configuration modal (WP-ALLOC-CONFIG-1) */}
      <ConfigurationModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
      />

      {/* Account target modal */}
      <AccountTargetModal
        isOpen={editingAccountId !== null}
        accountId={editingAccountId}
        onClose={() => setEditingAccountId(null)}
      />

      {/* Classify holding modal */}
      <ClassifyHoldingModal
        isOpen={classifyingHoldingId !== null}
        holdingId={classifyingHoldingId}
        onClose={() => setClassifyingHoldingId(null)}
      />

      {/* Bulk classify modal */}
      <BulkClassifyModal
        isOpen={showBulkClassifyModal}
        onClose={() => setShowBulkClassifyModal(false)}
      />

      {/* Bucket assignment modal */}
      <BucketAssignmentModal
        isOpen={assigningAccountId !== null}
        accountId={assigningAccountId === 'new' ? undefined : assigningAccountId ?? undefined}
        onClose={() => setAssigningAccountId(null)}
      />
    </div>
  );
}

interface PageHeaderProps {
  onTemplateClick: () => void;
  onRefreshClick: () => void;
  onConfigureClick: () => void;
}

function PageHeader({ onTemplateClick, onRefreshClick, onConfigureClick }: PageHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Allocation</h1>
        <p className="mt-1 text-gray-600">
          Track and manage your portfolio allocation
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onConfigureClick}
          leftIcon={<SettingsIcon className="h-4 w-4" />}
        >
          Configure
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onTemplateClick}
          leftIcon={<TemplateIcon className="h-4 w-4" />}
        >
          Use Template
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefreshClick}
          leftIcon={<RefreshIcon className="h-4 w-4" />}
        >
          Refresh
        </Button>
      </div>
    </div>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
      />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}
